import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Eye, 
  Download, 
  FileText, 
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Maximize
} from 'lucide-react';
import { RevisionSession } from '@/types/resumeRevision';

interface Step4PreviewProps {
  session: RevisionSession;
}

export const Step4Preview: React.FC<Step4PreviewProps> = ({ session }) => {
  const [previewMode, setPreviewMode] = useState<'clean' | 'highlighted'>('clean');
  const [zoomLevel, setZoomLevel] = useState(100);

  // Mock preview content - in real implementation, this would be generated from the session
  const mockResumeContent = `
    <div class="resume-preview">
      <header class="text-center mb-6">
        <h1 class="text-2xl font-bold">John Doe</h1>
        <p class="text-gray-600">Software Developer</p>
        <p class="text-sm">john.doe@email.com | (555) 123-4567 | LinkedIn</p>
      </header>
      
      <section class="mb-4">
        <h2 class="text-lg font-semibold border-b mb-2">Professional Summary</h2>
        <p class="text-sm">
          Results-driven software developer with 5+ years of experience in full-stack development.
          Proficient in modern technologies and passionate about creating efficient, scalable solutions.
        </p>
      </section>
      
      <section class="mb-4">
        <h2 class="text-lg font-semibold border-b mb-2">Skills</h2>
        <p class="text-sm">
          <span class="highlight-change">JavaScript, HTML, CSS, Node.js, Python, React</span>
        </p>
      </section>
      
      <section class="mb-4">
        <h2 class="text-lg font-semibold border-b mb-2">Experience</h2>
        <div class="mb-3">
          <h3 class="font-medium">Senior Developer | TechCorp</h3>
          <p class="text-xs text-gray-600 mb-1">Jan 2020 - Present</p>
          <ul class="text-sm list-disc list-inside">
            <li>Led development of web applications using React and Node.js</li>
            <li>Improved application performance by 40% through code optimization</li>
            <li>Collaborated with cross-functional teams to deliver projects on time</li>
          </ul>
        </div>
      </section>
    </div>
  `;

  const handleZoomIn = () => {
    if (zoomLevel < 150) setZoomLevel(prev => prev + 10);
  };

  const handleZoomOut = () => {
    if (zoomLevel > 50) setZoomLevel(prev => prev - 10);
  };

  const handleDownload = (format: 'pdf' | 'docx') => {
    // TODO: Implement download functionality
    console.log(`Downloading resume as ${format}`);
  };

  const handleRollback = () => {
    // TODO: Implement rollback to previous version
    console.log('Rolling back to original version');
  };

  const appliedChanges = session.change_items?.filter(item => item.status === 'completed') || [];

  return (
    <div className="space-y-6">
      {/* Preview Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Resume Preview
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Review your revised resume before finalizing the changes.
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {appliedChanges.length} changes applied
              </Badge>
              <Button variant="outline" size="sm" onClick={handleRollback}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Rollback
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Preview Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <Tabs value={previewMode} onValueChange={(value) => setPreviewMode(value as any)}>
              <TabsList>
                <TabsTrigger value="clean">Clean Version</TabsTrigger>
                <TabsTrigger value="highlighted">With Changes Highlighted</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={handleZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm min-w-16 text-center">{zoomLevel}%</span>
                <Button variant="outline" size="sm" onClick={handleZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              
              <Button variant="outline" size="sm">
                <Maximize className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Preview */}
        <div className="lg:col-span-2">
          <Card className="min-h-96">
            <CardContent className="p-0">
              <div className="bg-white border rounded-lg overflow-hidden">
                <div 
                  className="p-8 min-h-96"
                  style={{ 
                    transform: `scale(${zoomLevel / 100})`,
                    transformOrigin: 'top left',
                    width: `${10000 / zoomLevel}%`
                  }}
                >
                  <style dangerouslySetInnerHTML={{
                    __html: `
                    .highlight-change {
                      background-color: ${previewMode === 'highlighted' ? '#fef3c7' : 'transparent'};
                      padding: ${previewMode === 'highlighted' ? '0 2px' : '0'};
                      border-radius: 2px;
                    }
                    .resume-preview {
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                      line-height: 1.5;
                      color: #1f2937;
                    }
                    .resume-preview h1 {
                      color: #111827;
                      margin-bottom: 0.5rem;
                    }
                    .resume-preview h2 {
                      color: #374151;
                      margin-bottom: 0.5rem;
                      padding-bottom: 0.25rem;
                      border-bottom: 1px solid #d1d5db;
                    }
                    .resume-preview h3 {
                      color: #111827;
                      margin-bottom: 0.25rem;
                    }
                  `}} />
                  <div dangerouslySetInnerHTML={{ __html: mockResumeContent }} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Applied Changes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Applied Changes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {appliedChanges.length > 0 ? (
                appliedChanges.map((change, index) => (
                  <div key={change.id} className="text-sm border-l-2 border-green-500 pl-3">
                    <p className="font-medium">{change.instruction_text}</p>
                    <p className="text-muted-foreground text-xs">
                      Section: {change.section_context || 'Unknown'}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No changes applied yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Download Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Download Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => handleDownload('pdf')}
              >
                <FileText className="h-4 w-4 mr-2" />
                Download as PDF
              </Button>
              
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => handleDownload('docx')}
              >
                <FileText className="h-4 w-4 mr-2" />
                Download as Word
              </Button>

              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  Available formats:
                </p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <Badge variant="secondary">PDF</Badge>
                  <Badge variant="secondary">DOCX</Badge>
                  <Badge variant="secondary">TXT</Badge>
                  <Badge variant="secondary">HTML</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Template Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Template Preserved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Original Format:</span>
                  <Badge variant="outline">Professional</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Font Style:</span>
                  <span className="text-muted-foreground">Sans-serif</span>
                </div>
                <div className="flex justify-between">
                  <span>Layout:</span>
                  <span className="text-muted-foreground">Single Column</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Footer */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Last updated: {new Date().toLocaleString()}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline">
                Make More Changes
              </Button>
              <Button>
                Approve & Continue
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};